import { For, Show } from "solid-js";
import { MAP_CHANGE_STATUS } from "../../enums/map";
import { mapStatusLabel } from "./mapLabels";
import {
  type MapPreviewAction,
  sequencedMapActions,
} from "./mapPreviewActions";
import {
  MAP_MIGRATION_WAVES,
  mapActionCategoryLabel,
  mapCountLabel,
  mapFutureBriefLabel,
} from "./mapPreviewConfig";

export function MapMigrationPlan(props: {
  actions: MapPreviewAction[];
  onReview: (id: string) => void;
}) {
  const accepted = () =>
    props.actions.filter(
      (action) => action.change.status === MAP_CHANGE_STATUS.ACCEPTED,
    ).length;
  const sequenced = () => sequencedMapActions(props.actions);
  const needsContext = () =>
    props.actions.filter((action) => action.wave === null);
  const pages = () =>
    new Set(
      props.actions.flatMap((action) =>
        action.change.pages.map((page) => page.id),
      ),
    ).size;
  return (
    <div class="map-migration-view">
      <section class="map-migration-intro">
        <div>
          <span class="signal-mini-kicker">Indicative sequence</span>
          <h2>Migration plan</h2>
          <p>
            Supported draft actions arranged into rough delivery waves. Sequence
            and ownership still require client and delivery-team input.
          </p>
        </div>
        <dl>
          <div>
            <dd>{props.actions.length}</dd>
            <dt>preview actions</dt>
          </div>
          <div>
            <dd>{pages()}</dd>
            <dt>represented pages</dt>
          </div>
          <div>
            <dd>{accepted()}</dd>
            <dt>accepted in this session</dt>
          </div>
        </dl>
      </section>

      <Show
        when={sequenced().length > 0}
        fallback={
          <section class="map-preview-empty is-large">
            <strong>No migration sequence can be derived yet.</strong>
            <p>
              The current scan needs at least one supported draft architecture
              action before delivery waves can be proposed.
            </p>
          </section>
        }
      >
        <div class="map-migration-waves">
          <For each={MAP_MIGRATION_WAVES}>
            {(wave, index) => {
              const rows = () =>
                sequenced().filter((action) => action.wave === wave.key);
              return (
                <section class="map-migration-wave">
                  <header>
                    <span>0{index() + 1}</span>
                    <div>
                      <h2>{wave.label}</h2>
                      <p>{wave.description}</p>
                    </div>
                    <strong>{rows().length}</strong>
                  </header>
                  <Show
                    when={rows().length > 0}
                    fallback={
                      <p class="map-wave-empty">
                        No current action belongs in this wave.
                      </p>
                    }
                  >
                    <ol>
                      <For each={rows()}>
                        {(action) => (
                          <li>
                            <span
                              class={`map-migration-category is-${action.category}`}
                            >
                              {action.category
                                ? mapActionCategoryLabel(action.category)
                                : "Needs context"}
                            </span>
                            <div>
                              <strong>{action.change.title}</strong>
                              <p>{action.change.summary}</p>
                              <small>
                                {mapCountLabel(
                                  action.change.pages.length,
                                  "page",
                                )}
                                {" / "}
                                {action.concernCount > 0
                                  ? `${mapCountLabel(
                                      action.concernCount,
                                      "causal concern",
                                    )} / `
                                  : ""}
                                {mapStatusLabel(
                                  action.change.status,
                                  action.change.adjusted,
                                )}
                              </small>
                            </div>
                            <div class="map-migration-handoff">
                              <span class="map-migration-brief-type">
                                {mapFutureBriefLabel(action.futureBriefLevel)}
                              </span>
                              <button
                                type="button"
                                onClick={() => props.onReview(action.change.id)}
                              >
                                Review action
                              </button>
                            </div>
                          </li>
                        )}
                      </For>
                    </ol>
                  </Show>
                </section>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={needsContext().length > 0}>
        <section class="map-needs-context is-migration">
          <header>
            <div>
              <span class="signal-mini-kicker">Unsequenced</span>
              <h2>Needs context</h2>
            </div>
            <strong>{needsContext().length}</strong>
          </header>
          <p>
            These planner outputs are intentionally outside migration waves
            until client context supports a clear architecture category.
          </p>
          <ul class="map-migration-needs-list">
            <For each={needsContext()}>
              {(action) => (
                <li>
                  <strong>{action.change.title}</strong>
                  <button
                    type="button"
                    onClick={() => props.onReview(action.change.id)}
                  >
                    Review context
                  </button>
                </li>
              )}
            </For>
          </ul>
        </section>
      </Show>

      <section class="map-brief-bridge">
        <div>
          <span class="signal-mini-kicker">Downstream handoff</span>
          <h2>Map organises the response; Brief defines the work</h2>
        </div>
        <p>
          Brief currently receives one generic Map-backed delivery handoff.
          Page, Flow and Architecture brief levels are future concepts, not
          supported Brief types in this Preview.
        </p>
        <ul>
          <li>Potential Page brief</li>
          <li>Potential Flow brief</li>
          <li>Potential Architecture brief</li>
        </ul>
      </section>
    </div>
  );
}
